import React, { Fragment } from 'react';
import {
    ButtonGroup,
    CardBody,
    CardTitle,
    CardSubtitle,
    CardFooter,
    UncontrolledTooltip,
    Badge,
} from 'reactstrap';
import { round } from 'lodash-es';
import cls from 'classnames';
import {
    faGripVertical,
    faTruck,
    faFlag,
    faPhone,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { weekdayLetter } from '@s20/i18n';
import { getShort } from '@s20/common/util/date';
import { FlexStartRow, SmallOutlineButton, toasty } from '../common/forms/FormElements';
import { Weekdays } from '../Common';
import { Skeleton } from '../common/Skeleton';
import LongTermStatusIndicator from './LongTermStatusIndicator';
import DailyAttendanceEventIndicator from './DailyAttendanceEventIndicator';
import { isCancelAssignmentTreatedAsUnassigned } from './Dispatch';

export default class StaffCard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            clientHeight: 0,
        };
    }

    componentDidMount() {
        const div = document.getElementById(`staffCard-${this.props.staff.id}`);

        this.setState({ clientHeight: (div?.getBoundingClientRect().height ?? 0) }); // eslint-disable-line
    }

    getFlagEquipment = () => {
        const { staff } = this.props;

        if (!staff.assignedEquipment) {
            return [];
        }

        return staff.assignedEquipment.filter(
            (e) => e.isGSF,
        );
    };

    getWorkTruckDetails = () => {
        const { staff } = this.props;

        if (!staff.assignedEquipment) {
            return null;
        }

        const workTrucks = staff.assignedEquipment.filter(
            (e) => e.isVehicle,
        );

        if (!workTrucks || workTrucks.length === 0) {
            return null;
        }

        return workTrucks[0];
    };

    isActive = (day) => {
        const { jobs, staff } = this.props;

        return jobs.find(
            (j) => !j.isNonFlagging && !!j.assignments[day.id].staff.find(
                (g) => !!g.assignment && g.assignment.id == staff.id,
            ),
        );
    };

    isCancelled = (day) => {
        const {
            cancellationJobs,
            staff,
            jobAssignmentCancellationTypeIdsThatPreventReassignment = [],
            jobAssignmentCancellationTypes,
        } = this.props;

        return cancellationJobs.find((j) => j.assignments[day.id].cancelledAssignments.find(
            (ca) => ca.assignedEmployeeId == staff.id
                && (this.isCancelledButVisible(ca)
                    || ca.thresholdCancel != 0
                    || jobAssignmentCancellationTypeIdsThatPreventReassignment.includes(
                        ca.jobAssignmentCancellationTypeId,
                    ))
                && !isCancelAssignmentTreatedAsUnassigned(
                    ca,
                    jobAssignmentCancellationTypes,
                ),
        ));
    };

    isCancelledButVisible = (cancelledAssignment) => {
        const { jobAssignmentCancellationTypes } = this.props;

        return (
            jobAssignmentCancellationTypes
            && jobAssignmentCancellationTypes.some(
                (jact) => jact.id
                    == cancelledAssignment.jobAssignmentCancellationTypeId
                    && jact.includeOnRosterView,
            )
        );
    };

    hoursWorked = (day) => {
        const { jobs, cancellationJobs, staff } = this.props;
        let total = 0;

        jobs?.forEach((job) => {
            const assignment = job.assignments?.[day.id]?.staff?.find(
                (s) => s.assignment?.id === staff.id,
            )?.assignment;

            if (assignment?.hoursWorked) {
                total += assignment?.hoursWorked;
            }
        });

        cancellationJobs?.forEach((cancelledJob) => {
            const cancelledAssignment = cancelledJob.assignments?.[day.id]?.cancelledAssignments?.find(
                (s) => s.assignedEmployeeId === staff.id && !s.isDeleted,
            );

            if (cancelledAssignment?.hoursWorked) {
                total += cancelledAssignment.hoursWorked;
            }
        });

        return round(total, 2);
    };

    isTimesheetSubmitted = (day) => {
        const { jobs, cancellationJobs, staff } = this.props;

        const submissions = jobs
            .map((job) => job.assignments[day.id]?.staff?.find((s) => s.assignment?.id === staff.id)?.assignment)
            .filter(Boolean);

        const cancelsubmissions = cancellationJobs
            .map((cancelledJob) => cancelledJob.assignments[day.id]?.cancelledAssignments?.find(
                (s) => s.assignedEmployeeId === staff.id)).filter(Boolean);

        return [...submissions, ...cancelsubmissions].some((a) => a?.isTimesheetSubmitted);
    };

    hasNonFlaggingTimesheet = (day) => {
        const { jobs, cancellationJobs, staff } = this.props;

        const submissions = jobs
            .map((job) => job.assignments[day.id]?.staff?.find((s) => s.assignment?.id === staff.id)?.assignment)
            .filter(Boolean);

        const cancelsubmissions = cancellationJobs
            .map((cancelledJob) => cancelledJob.assignments[day.id]?.cancelledAssignments?.find(
                (s) => s.assignedEmployeeId === staff.id)).filter(Boolean);

        return [...submissions, ...cancelsubmissions].some((a) => a?.hasNonFlaggingTimesheet);
    };

    getWeeklyHours = () => {
        const { jobs, cancellationJobs, staff } = this.props;

        let total = 0;
        jobs?.forEach((job) => {
            const assignments = job.assignments || {};
            Object.values(assignments).forEach((assignmentDay) => {
                const staffList = assignmentDay.staff || [];

                staffList.forEach((s) => {
                    const { assignment } = s;
                    if (assignment?.id === staff.id && assignment.hoursWorked) {
                        total += assignment.hoursWorked;
                    }
                });
            });
        });

        cancellationJobs?.forEach((cancelledJob) => {
            const assignments = cancelledJob.assignments || {};

            Object.values(assignments).forEach((assignmentDay) => {
                const staffList = assignmentDay.cancelledAssignments || [];

                staffList.forEach((s) => {
                    if (s.assignedEmployeeId === staff.id && !s.isDeleted && s.hoursWorked) {
                        total += s.hoursWorked;
                    }
                });
            });
        });

        return round(total, 2);
    };


    render() {
        const {
            staff,
            provided,
            innerRef,
            longTermStatuses,
            employeeLongTermStatuses,
            dailyAttendanceEvents,
            employeeDailyAttendanceEvents,
            hiddenStaffIds,
            weekdays,
            compact,
            minimal,
            dragging,
            tenantSettings,
            disableStatus,
            certifications,
            selected = false,
            onClick,
            loading,
            selectedWeekRangeDays,
        } = this.props;

        const workTruckDetails = this.getWorkTruckDetails();
        const flagEquipment = this.getFlagEquipment();
        const weeklyHours = this.getWeeklyHours();
        const hoursEnabled = tenantSettings.dispatchHoursEnabled;

        if (hiddenStaffIds.includes(staff.id)) {
            return (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={innerRef}
                />
            );
        }
        const Component = dragging ? 'div' : 'dd';
        const PlaceholderComponent = dragging ? 'dd' : 'div';

        return (
            <>
                <Component
                    id={`staffCard-${staff.id}`}
                    className={cls('card staffCard', {
                        minimal: !!minimal,
                        dragging,
                        disabled: disableStatus,
                        selected,
                    })}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={innerRef}
                    onClick={onClick}
                    onDoubleClick={this.props.onDoubleClick}
                >

                    <div className="staff-card-main">
                        {!compact && (
                            <div className="dragIndicator">
                                <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                        )}
                        <CardBody>
                            <CardTitle className="card-title">
                                <span className="staff-info">
                                    <Badge
                                        color="secondary"
                                        className="cursor-pointer mr-1"
                                        id={`groups-${staff.id}`}
                                    >
                                        {staff.groups.map((g) => g.groupName.charAt(0).toUpperCase()).join(', ')}
                                    </Badge>

                                    <UncontrolledTooltip target={`groups-${staff.id}`}>
                                        {staff.groups.map((g) => g.groupName).join(', ')}
                                    </UncontrolledTooltip>

                                    <span
                                        id={`staff-name-${staff.id}`}
                                        className="staff-name"
                                    >
                                        {staff.name}
                                    </span>
                                    <UncontrolledTooltip target={`staff-name-${staff.id}`}>
                                        {staff.name}
                                    </UncontrolledTooltip>
                                </span>

                                <div className="status-icons">
                                    <DailyAttendanceEventIndicator
                                        staff={staff}
                                        dailyAttendanceEvents={dailyAttendanceEvents}
                                        employeeDailyAttendanceEvents={
                                            employeeDailyAttendanceEvents
                                        }
                                    />

                                    <LongTermStatusIndicator
                                        staff={staff}
                                        longTermStatuses={longTermStatuses}
                                        employeeLongTermStatuses={
                                            employeeLongTermStatuses
                                        }
                                        selectedWeekRangeDays={selectedWeekRangeDays}
                                    />
                                </div>
                            </CardTitle>

                            <CardSubtitle className="mb-0 mt-1">
                                <FlexStartRow className="align-items-center">
                                    {loading && hoursEnabled ? <Skeleton /> : (
                                        <>
                                            <ButtonGroup>
                                                {weekdays.map((dayDate) => {
                                                    const day = Weekdays.find(
                                                        (x) => x.id === dayDate.getDay(),
                                                    );

                                                    const hours = hoursEnabled ? this.hoursWorked(day) : 0;
                                                    const isSubmitted = hoursEnabled ? this.isTimesheetSubmitted(day) : false;
                                                    const hasNonFlaggingTimesheet = hoursEnabled ? this.hasNonFlaggingTimesheet(day) : false;

                                                    return (
                                                        <React.Fragment key={day.id}>
                                                            <span
                                                                id={`hours-${staff.id}-${day.id}`}
                                                                className={cls(
                                                                    'btn btn-sm btn-outline-secondary scheduledDayIndicator',
                                                                    {
                                                                        active: this.isActive(day),
                                                                        cancelled: this.isCancelled(day),
                                                                        'bg-info': isSubmitted,
                                                                        'nonFlagging': hasNonFlaggingTimesheet,
                                                                    },
                                                                )}
                                                            >
                                                                {weekdayLetter(day.id)}
                                                            </span>

                                                            {Boolean(isSubmitted || hasNonFlaggingTimesheet) && hours > 0 && (
                                                                <UncontrolledTooltip
                                                                    target={`hours-${staff.id}-${day.id}`}
                                                                    fade={false}
                                                                >
                                                                    {hours}h
                                                                </UncontrolledTooltip>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </ButtonGroup>

                                            {Boolean(hoursEnabled) && (
                                                <div className="hours-badge-container d-inline-flex">
                                                    <Badge
                                                        color={weeklyHours >= 40 ? 'warning' : 'secondary'}
                                                        className="ml-1 hours-badge"
                                                    >
                                                        {weeklyHours}
                                                        h
                                                    </Badge>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </FlexStartRow>
                            </CardSubtitle>

                            <CardFooter
                                className="mt-2 d-flex align-items-center flex-wrap"
                                style={{ rowGap: '0.35rem', columnGap: '0.25rem' }}
                            >
                                {Boolean(staff.homeLocalNumber?.toString().trim()) && (
                                    <>
                                        <Badge
                                            id={`home-local-${staff.id}`}
                                            color="secondary"
                                        >
                                            {staff.homeLocalNumber}
                                        </Badge>
                                        <UncontrolledTooltip target={`home-local-${staff.id}`}>
                                            {staff.homeLocalName}
                                        </UncontrolledTooltip>
                                    </>
                                )}

                                {Boolean(workTruckDetails) && (
                                    <div
                                        id={`truck-${staff.id}-${workTruckDetails.id}`}
                                        className="cursor-pointer"
                                    >
                                        <FontAwesomeIcon icon={faTruck} />

                                        <UncontrolledTooltip target={`truck-${staff.id}-${workTruckDetails.id}`}>
                                            {workTruckDetails.equipmentType.description} - {workTruckDetails.identifier}
                                        </UncontrolledTooltip>
                                    </div>
                                )}

                                {flagEquipment?.length > 0 && flagEquipment.map((flagEquipmentDetails) => (
                                    <div
                                        className="cursor-pointer"
                                        key={flagEquipmentDetails.id}
                                        id={`flag-${staff.id}-${flagEquipmentDetails.id}`}
                                    >
                                        <FontAwesomeIcon icon={faFlag} />

                                        <UncontrolledTooltip target={`flag-${staff.id}-${flagEquipmentDetails.id}`}>
                                            {flagEquipmentDetails.equipmentType.description} - {flagEquipmentDetails.identifier}
                                        </UncontrolledTooltip>
                                    </div>
                                ))}


                                {staff.employeeCertifications?.length > 0 && staff.employeeCertifications
                                    ?.filter((cert) => cert.certification.showOnDispatchBoard)
                                    .map((cert) => (

                                        <Fragment key={cert.id}>
                                            <Badge
                                                key={cert.id}
                                                id={`cert-${cert.id}`}
                                                color="info"
                                            >
                                                {cert.certification.abbreviation}
                                            </Badge>

                                            <UncontrolledTooltip
                                                target={`cert-${cert.id}`}
                                            >
                                                {cert.certification.name}{Boolean(cert.expirationDate) && ` - Expires on ${getShort(cert.expirationDate)}`}
                                            </UncontrolledTooltip>
                                        </Fragment>
                                    ),
                                    )}
                                {certifications
                                    ?.filter((c) => c.mustBeValidForDispatch)
                                    .map((cert) => {
                                        const hasCert = staff.employeeCertifications?.some(
                                            (empCert) => empCert.certificationId === cert.id,
                                        );
                                        if (hasCert) return null;
                                        return (
                                            <Badge
                                                key={cert.id}
                                                id={`missing-cert-${cert.id}`}
                                                color="danger"
                                            >
                                                {cert.abbreviation}
                                            </Badge>
                                        );
                                    })}

                            </CardFooter>

                        </CardBody>
                    </div>

                    {Boolean(staff.mobilePhone) && (
                        <div className="staff-card-meta-footer d-flex align-items-center flex-wrap">
                            <SmallOutlineButton
                                color="outline-dark"
                                className="staff-phone-link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(staff.mobilePhone)
                                        .then(() => toasty.success(`Copied ${staff.mobilePhone} to clipboard`))
                                        .catch(() => toasty.error('Failed to copy phone number'));
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPhone}
                                    className="mr-1"
                                />
                                {staff.mobilePhone}
                            </SmallOutlineButton>
                        </div>
                    )}
                </Component>

                {!compact && (
                    <PlaceholderComponent
                        className="card staffCard placeholder"
                        style={{ height: this.state.clientHeight }}
                    />
                )}
            </>
        );
    }
}
